/**
 * V167.INVITE: Centralized invite storage helper
 * Ensures invite context persists through OAuth, onboarding, and profile navigation
 * Uses multiple redundancy strategies to prevent data loss
 *
 * Logging is redacted — never logs invite codes, URLs, or storage dumps.
 */

import { logInviteSafe } from '@/utils/safeInviteLogging';

export interface InviteContext {
  inviteCode: string;
  inviterId: string;
  inviterName: string;
  inviterAvatar?: string;
  timestamp?: string;
}

export interface PopupData {
  inviteCode: string;
  inviterId: string;
  inviterName: string;
  inviterAvatar?: string;
  userId?: string;
}

const PENDING_INVITE_KEYS = [
  'pendingInviteCode',
  'postOnboardingInviteData',
  'showPostOnboardingInvitePopup',
] as const;

const LEGACY_INVITE_KEYS = [
  'pendingInviteContext',
  'inviteChoice',
  'inviteCodeTimestamp',
  'pendingInviteContextRehydrateInFlight',
  'inviteConversionInProgress',
] as const;

const LEGACY_KEY_PREFIXES = ['inviteModalShown_', 'blissnet_invite_route_terminal_v1:'] as const;

const DISMISSED_INVITES_SESSION_KEY = 'dismissedInviteCodesSession';

function removeStorageKeysMatchingPrefixes(storage: Storage, prefixes: readonly string[]): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key) continue;
    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
}

/** Canonical 3-key cleanup + broadcast for listeners to abort in-flight invite processing. */
export function clearPendingInviteState(): void {
  logInviteSafe('INVITE_STORAGE', { action: 'cleared' });
  for (const key of PENDING_INVITE_KEYS) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
  window.dispatchEvent(new CustomEvent('invite-storage-cleared'));
}

/** Removes legacy invite keys and session guard keys that can resurrect popups. */
export function clearInviteLegacyState(): void {
  for (const key of LEGACY_INVITE_KEYS) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
  removeStorageKeysMatchingPrefixes(localStorage, LEGACY_KEY_PREFIXES);
  removeStorageKeysMatchingPrefixes(sessionStorage, LEGACY_KEY_PREFIXES);
}

export function markInviteDismissed(inviteCode: string): void {
  if (!inviteCode) return;
  try {
    const raw = sessionStorage.getItem(DISMISSED_INVITES_SESSION_KEY);
    const dismissed: string[] = raw ? JSON.parse(raw) : [];
    if (!dismissed.includes(inviteCode)) {
      dismissed.push(inviteCode);
      sessionStorage.setItem(DISMISSED_INVITES_SESSION_KEY, JSON.stringify(dismissed));
    }
  } catch {
    sessionStorage.setItem(DISMISSED_INVITES_SESSION_KEY, JSON.stringify([inviteCode]));
  }
}

export function wasInviteDismissed(inviteCode: string): boolean {
  if (!inviteCode) return false;
  try {
    const raw = sessionStorage.getItem(DISMISSED_INVITES_SESSION_KEY);
    if (!raw) return false;
    const dismissed: string[] = JSON.parse(raw);
    return dismissed.includes(inviteCode);
  } catch {
    return false;
  }
}

export const InviteStorageHelper = {
  /**
   * Store invite code with maximum redundancy
   */
  storeInviteCode(inviteCode: string): void {
    localStorage.setItem('pendingInviteCode', inviteCode);
    sessionStorage.setItem('pendingInviteCode', inviteCode);

    const timestamp = new Date().toISOString();
    localStorage.setItem('inviteCodeTimestamp', timestamp);
    sessionStorage.setItem('inviteCodeTimestamp', timestamp);

    logInviteSafe('INVITE_STORAGE', { action: 'saved' });
  },

  /**
   * Retrieve invite code from any available source
   */
  getInviteCode(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('inviteCode');

    const fromLocal = localStorage.getItem('pendingInviteCode');
    const fromSession = sessionStorage.getItem('pendingInviteCode');

    const inviteCode = fromUrl || fromLocal || fromSession;

    logInviteSafe('INVITE_STORAGE', {
      action: 'read',
      fromUrl: !!fromUrl,
      fromLocal: !!fromLocal,
      fromSession: !!fromSession,
      hasCode: !!inviteCode,
    });

    return inviteCode;
  },

  /**
   * Store full invite context with maximum redundancy
   */
  storeInviteContext(context: InviteContext): void {
    const contextWithTimestamp = {
      ...context,
      timestamp: new Date().toISOString()
    };

    const contextStr = JSON.stringify(contextWithTimestamp);

    localStorage.setItem('pendingInviteContext', contextStr);
    sessionStorage.setItem('pendingInviteContext', contextStr);

    localStorage.setItem('inviteChoice', 'connect');
    sessionStorage.setItem('inviteChoice', 'connect');

    logInviteSafe('INVITE_STORAGE', { action: 'context_saved' });
  },

  /**
   * Get full invite context from any available source
   */
  getInviteContext(): InviteContext | null {
    const fromLocal = localStorage.getItem('pendingInviteContext');
    const fromSession = sessionStorage.getItem('pendingInviteContext');

    const contextStr = fromLocal || fromSession;

    if (contextStr) {
      try {
        const context = JSON.parse(contextStr);
        logInviteSafe('INVITE_STORAGE', { action: 'context_read', hasContext: true });
        return context;
      } catch {
        logInviteSafe('INVITE_STORAGE', { action: 'parse_error' });
        return null;
      }
    }

    logInviteSafe('INVITE_STORAGE', { action: 'context_missing' });
    return null;
  },

  /**
   * Store popup data for post-onboarding display
   */
  storePopupData(data: PopupData): void {
    localStorage.setItem('showPostOnboardingInvitePopup', 'true');
    sessionStorage.setItem('showPostOnboardingInvitePopup', 'true');

    const dataStr = JSON.stringify({ ...data, storedAt: new Date().toISOString() });
    localStorage.setItem('postOnboardingInviteData', dataStr);
    sessionStorage.setItem('postOnboardingInviteData', dataStr);
    window.dispatchEvent(new CustomEvent('invite-storage-ready'));

    logInviteSafe('INVITE_STORAGE', { action: 'popup_saved' });
  },

  /**
   * Get popup data for display
   */
  getPopupData(): { shouldShow: boolean; data: PopupData | null } {
    const shouldShowLocal = localStorage.getItem('showPostOnboardingInvitePopup');
    const shouldShowSession = sessionStorage.getItem('showPostOnboardingInvitePopup');
    const dataLocal = localStorage.getItem('postOnboardingInviteData');
    const dataSession = sessionStorage.getItem('postOnboardingInviteData');

    const shouldShow = shouldShowLocal === 'true' || shouldShowSession === 'true';
    const dataStr = dataLocal || dataSession;

    let data: PopupData | null = null;
    if (dataStr) {
      try {
        data = JSON.parse(dataStr);
      } catch {
        logInviteSafe('INVITE_STORAGE', { action: 'parse_error' });
      }
    }

    logInviteSafe('INVITE_STORAGE', {
      action: 'popup_read',
      shouldShow,
      hasData: !!data,
    });

    return { shouldShow, data };
  },

  /**
   * Check if invite context exists
   */
  hasInviteContext(): boolean {
    const inviteCode = this.getInviteCode();
    const inviteContext = this.getInviteContext();
    const hasContext = !!(inviteCode || inviteContext);

    logInviteSafe('INVITE_STORAGE', { action: 'read', hasContext });
    return hasContext;
  },

  /**
   * Clean up all invite data after successful connection
   */
  cleanup(): void {
    localStorage.removeItem('pendingInviteCode');
    localStorage.removeItem('pendingInviteContext');
    localStorage.removeItem('showPostOnboardingInvitePopup');
    localStorage.removeItem('postOnboardingInviteData');
    localStorage.removeItem('inviteCodeTimestamp');
    localStorage.removeItem('inviteChoice');

    sessionStorage.removeItem('pendingInviteCode');
    sessionStorage.removeItem('pendingInviteContext');
    sessionStorage.removeItem('showPostOnboardingInvitePopup');
    sessionStorage.removeItem('postOnboardingInviteData');
    sessionStorage.removeItem('inviteCodeTimestamp');
    sessionStorage.removeItem('inviteChoice');

    logInviteSafe('INVITE_STORAGE', { action: 'cleared' });
  },

  /**
   * Safe storage presence snapshot — never dumps values.
   */
  debugPrintStorage(): void {
    logInviteSafe('INVITE_STORAGE', {
      action: 'storage_snapshot',
      hasPendingCode: !!(
        localStorage.getItem('pendingInviteCode') || sessionStorage.getItem('pendingInviteCode')
      ),
      hasPendingContext: !!(
        localStorage.getItem('pendingInviteContext') || sessionStorage.getItem('pendingInviteContext')
      ),
      hasPopupFlag: !!(
        localStorage.getItem('showPostOnboardingInvitePopup') ||
        sessionStorage.getItem('showPostOnboardingInvitePopup')
      ),
      hasPopupData: !!(
        localStorage.getItem('postOnboardingInviteData') ||
        sessionStorage.getItem('postOnboardingInviteData')
      ),
      hasInviteChoice: !!(
        localStorage.getItem('inviteChoice') || sessionStorage.getItem('inviteChoice')
      ),
      hasUrlInviteParam: !!new URLSearchParams(window.location.search).get('inviteCode'),
    });
  }
};
